<script setup lang="ts">
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Filler,
  type Plugin,
} from 'chart.js'
import { Line } from 'vue-chartjs'
import 'chartjs-adapter-moment'
import { computed } from 'vue'
import { formatCurrency } from '@/modules/shared/utils/currency'

ChartJS.register(LineElement, PointElement, LinearScale, TimeScale, Tooltip, Filler)

const props = defineProps<{
  points: { date: string; value_amount: number }[]
  currency?: string
  loading?: boolean
}>()

const LINE = '#3f7d7d'
const END_FILL = '#6cae5e'
const END_HALO = 'rgba(108, 174, 94, 0.28)'

const displayPoints = computed(() => props.points)

const lastIndex = computed(() => displayPoints.value.length - 1)

const chartData = computed(() => ({
  datasets: [
    {
      label: 'Current Value',
      data: displayPoints.value.map((p) => ({ x: p.date, y: p.value_amount })),
      borderColor: LINE,
      borderWidth: 2.5,
      cubicInterpolationMode: 'monotone' as const,
      tension: 0.4,
      fill: true,
      backgroundColor: (ctx: any) => {
        const { chart } = ctx
        const { ctx: c, chartArea } = chart
        if (!chartArea) return 'rgba(120, 165, 150, 0)'
        const g = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
        g.addColorStop(0, 'rgba(120, 165, 150, 0.30)')
        g.addColorStop(0.6, 'rgba(120, 165, 150, 0.08)')
        g.addColorStop(1, 'rgba(120, 165, 150, 0)')
        return g
      },
      pointRadius: displayPoints.value.map((_, i) => (i === 0 || i === lastIndex.value ? 5 : 0)),
      pointHoverRadius: displayPoints.value.map((_, i) => (i === 0 || i === lastIndex.value ? 6 : 3)),
      pointBackgroundColor: displayPoints.value.map((_, i) => (i === lastIndex.value ? END_FILL : '#ffffff')),
      pointBorderColor: displayPoints.value.map((_, i) => (i === lastIndex.value ? END_HALO : LINE)),
      pointBorderWidth: displayPoints.value.map((_, i) => (i === lastIndex.value ? 6 : i === 0 ? 2 : 0)),
    },
  ],
}))

const dashedGrid: Plugin = {
  id: 'dashedGrid',
  beforeDatasetsDraw(chart) {
    const { ctx, chartArea, scales } = chart
    const y = scales.y as any
    if (!y || !chartArea) return
    ctx.save()
    ctx.setLineDash([4, 4])
    ctx.lineWidth = 1
    ctx.strokeStyle = 'rgba(15, 23, 42, 0.07)'
    for (const tick of y.ticks) {
      const yPos = y.getPixelForValue(tick.value)
      ctx.beginPath()
      ctx.moveTo(chartArea.left, yPos)
      ctx.lineTo(chartArea.right, yPos)
      ctx.stroke()
    }
    ctx.restore()
  },
}

const options = computed(
  () =>
    ({
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 16, bottom: 4 } },
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          displayColors: false,
          callbacks: { label: (c: any) => formatCurrency(c.parsed.y, { currency: props.currency ?? 'USD' }) },
        },
      },
      scales: {
        x: {
          type: 'time',
          time: {
            unit: 'month',
            displayFormats: { month: "MMM [']YY" },
            tooltipFormat: 'MMM D, YYYY',
          },
          grid: { display: false },
          border: { display: false },
          ticks: {
            color: '#94a3b8',
            font: { size: 12 },
            maxRotation: 0,
            autoSkip: true,
            autoSkipPadding: 24,
          },
        },
        y: {
          grace: '15%',
          grid: { display: false },
          border: { display: false },
          ticks: { display: false, maxTicksLimit: 4 },
        },
      },
    }) as any,
)
</script>

<template>
  <div>
    <div class="mb-3">
      <h3 class="text-sm font-medium text-slate-700">Portfolio Value Over Time</h3>
    </div>
    <div class="relative h-[300px] w-full">
      <Line v-if="displayPoints.length" :data="chartData" :options="options" :plugins="[dashedGrid]" />
      <div v-else-if="!loading" class="flex h-full items-center justify-center text-sm text-slate-400">No data</div>
      <div v-if="loading" class="absolute inset-0 flex items-center justify-center bg-white/60" aria-busy="true">
        <span class="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-slate-500" />
      </div>
    </div>
  </div>
</template>
